 
import React, { useState } from 'react';
import {
  ArrowLeft, User, Phone, MapPin, CreditCard, Banknote, Building2,
  Upload, Trash2, Star
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { compressImage } from '../../utils/imageCompressor';
import { formatAmount } from '../../utils/format';
import { calcTotals, itbisLabel } from '../../utils/tax';
const CustomerCheckoutView = ({
  cart, tenant, user, userAddresses, loyaltyPoints, 
  onConfirmOrder, onBack
}) => {
  const { subtotal, tax, total } = calcTotals(cart.reduce((sum, item) => sum + (item.price * item.quantity), 0), tenant);

  const [customerInfo, setCustomerInfo] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: '',
  });
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transferProof, setTransferProof] = useState(null);
  const [transferProofPreview, setTransferProofPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTransferProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Comprimir en el navegador (WebP) para aceptar cualquier tamano. Si no es
    // imagen (ej. PDF), compressImage lo devuelve tal cual.
    const compressedFile = await compressImage(file, { maxSize: 1000 });
    setTransferProof(compressedFile);
    setTransferProofPreview(URL.createObjectURL(compressedFile));
  };

  const handleSubmit = async () => {
    if (!customerInfo.name || !customerInfo.phone || !customerInfo.address) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    if (paymentMethod === 'transfer' && !transferProof) {
      toast.error('Por favor sube el comprobante de transferencia');
      return;
    }
    setLoading(true);
    await onConfirmOrder({ ...customerInfo, paymentMethod, transferProof, total });
    setLoading(false);
  };

  return (
    <div className="pb-4 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-gray-100">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full active:scale-90 transition-transform">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">Checkout</h1>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 max-w-lg mx-auto">
        {/* Loyalty Points */}
        {loyaltyPoints !== null && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
            <Star className="w-8 h-8 text-amber-500 fill-amber-500" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Tus Puntos de Fidelidad</p>
              <p className="text-2xl font-bold text-amber-600">{loyaltyPoints} pts</p>
            </div>
          </div>
        )}

        {/* Customer Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
          <h3 className="font-semibold text-gray-800">Información de Entrega</h3>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-orange-500" /> Nombre Completo
            </Label>
            <Input
              value={customerInfo.name}
              onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
              placeholder="Tu nombre"
              className="rounded-xl h-11 bg-gray-50 border-0"
              disabled={!!user?.name}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <Phone className="w-3.5 h-3.5 text-orange-500" /> Teléfono
            </Label>
            <Input
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
              placeholder="Tu teléfono"
              className="rounded-xl h-11 bg-gray-50 border-0"
              disabled={!!user?.phone}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-orange-500" /> Dirección
            </Label>
            {user && userAddresses?.length > 0 ? (
              <Select
                value={selectedAddress?.address || ''}
                onValueChange={(value) => {
                  const addr = userAddresses.find(a => a.address === value);
                  setSelectedAddress(addr);
                  setCustomerInfo({ ...customerInfo, address: value });
                }}
              >
                <SelectTrigger className="rounded-xl h-11 bg-gray-50 border-0">
                  <SelectValue placeholder="Selecciona una dirección" />
                </SelectTrigger>
                <SelectContent>
                  {userAddresses.map((addr, i) => (
                    <SelectItem key={i} value={addr.address}>
                      <div className="flex items-center gap-2">
                        <span>{addr.label || 'Dirección'}</span>
                        {addr.isDefault && <Badge variant="outline" className="text-[10px]">Predet.</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={customerInfo.address}
                onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                placeholder="Dirección completa"
                className="rounded-xl h-11 bg-gray-50 border-0"
              />
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <h3 className="font-semibold text-gray-800">Método de Pago</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'cash', label: 'Efectivo', icon: Banknote, color: 'text-green-500' },
              { value: 'card', label: 'Tarjeta', icon: CreditCard, color: 'text-blue-500' },
              { value: 'transfer', label: 'Transferencia', icon: Building2, color: 'text-purple-500' },
            ].map(({ value, label, icon: Icon, color }) => (
              <button
                key={value}
                onClick={() => setPaymentMethod(value)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all active:scale-95 ${
                  paymentMethod === value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-100 bg-gray-50'
                }`}
              >
                <Icon className={`w-6 h-6 ${paymentMethod === value ? 'text-orange-500' : color}`} />
                <span className={`text-xs font-medium ${paymentMethod === value ? 'text-orange-600' : 'text-gray-600'}`}>
                  {label}
                </span>
              </button>
            ))}
          </div>

          {paymentMethod === 'transfer' && tenant?.bank_account && (
            <div className="bg-blue-50 rounded-xl p-4 space-y-2 mt-3">
              <h4 className="font-semibold text-sm text-gray-800">Datos Bancarios</h4>
              <div className="text-xs space-y-1 text-gray-600">
                <p><span className="font-medium">Banco:</span> {tenant.bank_name || 'Banco Nacional'}</p>
                <p><span className="font-medium">Cuenta:</span> {tenant.bank_account}</p>
                <p><span className="font-medium">Titular:</span> {tenant.bank_account_holder || tenant.name}</p>
                <p className="text-orange-600 font-bold text-sm mt-2">Monto: ${formatAmount(total)}</p>
              </div>
              <div className="mt-3">
                <Label className="text-xs font-medium text-gray-600 mb-1 block">
                  <Upload className="w-3 h-3 inline mr-1" /> Comprobante
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleTransferProofUpload}
                  className="rounded-xl bg-white text-sm"
                />
                {transferProofPreview && (
                  <div className="relative mt-2">
                    <img src={transferProofPreview} alt="Comprobante" className="w-full h-32 object-cover rounded-xl" />
                    <button
                      onClick={() => { setTransferProof(null); setTransferProofPreview(null); }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-2">
          <h3 className="font-semibold text-gray-800 mb-2">Resumen</h3>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Subtotal ({cart.length} items)</span>
            <span className="font-medium">${formatAmount(subtotal)}</span>
          </div>
          {tax > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{itbisLabel()}</span>
              <span className="font-medium">${formatAmount(tax)}</span>
            </div>
          )}
          <div className="border-t pt-2 mt-2 flex justify-between">
            <span className="font-bold text-gray-800">Total</span>
            <span className="text-xl font-bold text-orange-600">${formatAmount(total)}</span>
          </div>
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold rounded-xl shadow-lg active:scale-[0.98] transition-transform text-base"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Procesando...
            </span>
          ) : (
            `Confirmar Pedido - $${formatAmount(total)}`
          )}
        </Button>
      </div>
    </div>
  );
};

export default CustomerCheckoutView;
